/*
  Animated product accordions.

  A native <details> gives no transition of its own, and the CSS-only route
  (::details-content + interpolate-size) only landed in Safari 18.4, so on an
  older iPhone the panel snapped open while the plus rotated. This drives the
  height explicitly with the Web Animations API instead, which behaves the same
  in every browser.

  The panel is .accordion__content. It needs `overflow: hidden` and border-box
  sizing so that height: 0 collapses its padding too — both set in
  pdp-info-column.css.

  Reference timing (measured on gruns.co): 300ms ease-in-out, matching the
  plus/minus rotation so the two move together.
*/
(() => {
  const DURATION = 300;
  const EASING = 'ease-in-out';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setup(details) {
    if (details.dataset.pdpAccordion === 'ready') return;
    const summary = details.querySelector('summary');
    const panel = details.querySelector('.accordion__content');
    if (!summary || !panel) return;
    details.dataset.pdpAccordion = 'ready';

    let animation = null;

    const clear = () => {
      panel.style.height = '';
      panel.style.paddingTop = '';
      panel.style.paddingBottom = '';
      animation = null;
    };

    // scrollHeight is the full border-box height including padding, so the
    // padding has to be animated alongside it. Height alone cannot reach 0: a
    // border-box element never renders shorter than its own padding, so the
    // panel stalled at its 18px padding-bottom before jumping the rest.
    const metrics = () => {
      const cs = getComputedStyle(panel);
      return { target: panel.scrollHeight, padTop: cs.paddingTop, padBottom: cs.paddingBottom };
    };

    summary.addEventListener('click', (event) => {
      // Let modified clicks and reduced-motion users fall through to the native
      // toggle rather than intercepting them.
      if (event.metaKey || event.ctrlKey || event.shiftKey || reduceMotion()) return;

      event.preventDefault();
      if (animation) animation.cancel();

      if (!details.open) {
        details.open = true;
        const { target, padTop, padBottom } = metrics();
        // Pin the collapsed state synchronously. Between `open = true` and the
        // animation's first frame the browser would otherwise paint the panel at
        // its full height for one frame — the flick at the start of opening.
        panel.style.height = '0px';
        panel.style.paddingTop = '0px';
        panel.style.paddingBottom = '0px';
        animation = panel.animate(
          {
            height: ['0px', `${target}px`],
            paddingTop: ['0px', padTop],
            paddingBottom: ['0px', padBottom],
          },
          { duration: DURATION, easing: EASING }
        );
        animation.onfinish = clear;
        animation.oncancel = clear;
      } else {
        // `open` has to stay true until the animation ends, so the [open] rule
        // would hold the minus at 90deg and snap it back at the finish. This
        // class reverses the bar now, in step with the panel.
        details.classList.add('is-closing');
        const { padTop, padBottom } = metrics();
        const start = panel.getBoundingClientRect().height;
        animation = panel.animate(
          {
            height: [`${start}px`, '0px'],
            paddingTop: [padTop, '0px'],
            paddingBottom: [padBottom, '0px'],
          },
          // fill: forwards holds the panel at 0 after the last frame. Without it
          // the animation stops applying before onfinish runs, so the panel
          // snapped back to its natural height for a frame — the flick at the
          // end of closing.
          { duration: DURATION, easing: EASING, fill: 'forwards' }
        );
        animation.onfinish = () => {
          const finished = animation;
          animation = null;
          details.open = false;
          details.classList.remove('is-closing');
          if (finished) finished.cancel(); // release the forwards fill
          clear();
        };
        animation.oncancel = () => {
          details.classList.remove('is-closing');
          clear();
        };
      }
    });
  }

  function init() {
    document
      .querySelectorAll(
        // buy-box accordions, their mobile copy above the FAQ, and the FAQ section share one component
        '.product__info-wrapper .product__accordion details, .pdp-accs .product__accordion details, .gs-faq__item details'
      )
      .forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // the section re-renders on variant change and in the theme editor
  document.addEventListener('shopify:section:load', init);
})();
